import { useState } from 'react';
import { ChevronDown, Mail, Phone, MapPin, Package } from 'lucide-react';

const STATUS_STYLES = {
  new: 'border-turmeric/50 bg-turmeric/10 text-turmeric',
  replied: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
  closed: 'border-white/15 bg-white/5 text-dune',
};

const formatDate = (iso) =>
  new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function EnquiryRow({ enquiry: e, onStatus, busy }) {
  const [open, setOpen] = useState(false);
  const status = e.status || 'new';

  return (
    <li className="rounded-2xl border border-white/10 bg-coal/40" data-testid={`enquiry-row-${e.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="grid w-full grid-cols-[1fr_auto] items-center gap-4 p-5 text-left md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto]"
        data-testid={`enquiry-toggle-${e.id}`}
      >
        <div className="min-w-0">
          <p className="truncate font-serif text-xl text-bone">{e.name}</p>
          <p className="truncate text-[12px] text-dune">{e.company || 'No company given'}</p>
        </div>
        <div className="hidden min-w-0 md:block">
          <p className="truncate text-sm text-bone/85">{e.spice}</p>
          <p className="truncate text-[12px] text-dune">{e.quantity}</p>
        </div>
        <div className="hidden text-[12px] text-dune md:block">{formatDate(e.created_at)}</div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${e.buyer_type === 'domestic' ? 'border-sky-400/40 text-sky-300' : 'border-bone/25 text-bone/80'}`} data-testid={`enquiry-buyer-${e.id}`}>
            {e.buyer_type}
          </span>
          <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[status]}`} data-testid={`enquiry-status-${e.id}`}>
            {status}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 text-dune transition-transform duration-300 ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} />
      </button>

      {open && (
        <div className="grid gap-6 border-t border-white/10 p-5 md:grid-cols-12" data-testid={`enquiry-detail-${e.id}`}>
          <div className="space-y-2 text-sm text-bone/85 md:col-span-4">
            <a href={`mailto:${e.email}`} className="flex items-center gap-2 hover:text-turmeric"><Mail className="h-3.5 w-3.5 text-turmeric" strokeWidth={1.5} />{e.email}</a>
            {e.phone && <a href={`tel:${e.phone}`} className="flex items-center gap-2 hover:text-turmeric"><Phone className="h-3.5 w-3.5 text-turmeric" strokeWidth={1.5} />{e.phone}</a>}
            <p className="flex items-center gap-2"><Package className="h-3.5 w-3.5 text-turmeric" strokeWidth={1.5} />{e.spice} · {e.quantity}</p>
            {e.destination && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-turmeric" strokeWidth={1.5} />{e.destination}</p>}
          </div>
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-dune">Specification &amp; notes</p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-bone/80">{e.message || '—'}</p>
            {e.replied_at && <p className="mt-3 text-[11px] text-dune">Replied {formatDate(e.replied_at)}</p>}
          </div>
          <div className="flex flex-wrap items-start gap-2 md:col-span-3 md:justify-end">
            {['new', 'replied', 'closed'].filter((s) => s !== status).map((s) => (
              <button
                key={s}
                type="button"
                disabled={busy}
                onClick={() => onStatus(e.id, s)}
                data-testid={`enquiry-mark-${s}-${e.id}`}
                className={`rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 disabled:opacity-50 ${
                  s === 'replied' ? 'border-turmeric bg-turmeric text-night hover:bg-bone' : 'border-white/15 text-bone/80 hover:border-turmeric hover:text-turmeric'
                }`}
              >
                Mark {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </li>
  );
}
